import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { Grid, TextField, Typography } from "@material-ui/core";

import { loginService } from "../../auth/services";
import { Participant, Request } from "../../models/api";
import { SubscriptionStatus } from "../../models/subscription-status";
import { updatePrimaryEmail } from "../../participant/service";
import { Modal, WarningText } from "../../shared/components/Modal";
import { ErrorMessages } from "../../shared/components/errorMessages";
import { getErrorMessageFromRequestError } from "../../shared/helpers/messages/request";
import { validateEmail } from "../../shared/validateEmail";
import { RootState } from "../../store";

export interface ParticipantEmail {
  request: Request;
  username: string;
}

export interface ParticipantEmailProps {
  participant: Participant;
  close: () => void;
  open: boolean;
  onAfter: (participantProfileId: string) => void;
  subscriptionStatus: SubscriptionStatus;
}

export interface ParticipantEmailErrorForm {
  message: string;
  key: string;
}

export const UpdateParticipantEmail = ({
  participant,
  close,
  open,
  onAfter,
  subscriptionStatus,
}: ParticipantEmailProps) => {
  const [email, setEmail] = useState("");
  const [errorsForm, setErrorsForm] = useState<
    [] | ParticipantEmailErrorForm[]
  >([]);
  const [password, setPassword] = useState("");
  const [currentStep, setCurrentStep] = useState(0);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    if (name === "email") {
      setEmail(value);
    } else if (name === "password") {
      setPassword(value);
    }
  };
  const { request, username } = useSelector<RootState, ParticipantEmail>(
    (state) => ({
      request: state.request.request,
      username: state.profile.email,
    })
  );

  const setErrorOnPasswordConfirmation = async (
    message = "Password is incorrect."
  ) => {
    setErrorsForm([
      {
        message,
        key: message,
      },
    ]);
  };

  const resetErrors = () => {
    setErrorsForm([]);
  };

  const validateCurrentPassword = useCallback(
    async (passwordState, usernameState) => {
      try {
        resetErrors();
        const response = await loginService({
          username: usernameState,
          password: passwordState,
        });

        if (response.status === 200) {
          await updatePrimaryEmail(usernameState);
          setCurrentStep(2);
        } else {
          await setErrorOnPasswordConfirmation();
        }
      } catch (e) {
        const message = getErrorMessageFromRequestError(e);
        await setErrorOnPasswordConfirmation(message);
      }
    },
    []
  );

  const onConfirm = async () => {
    if (currentStep === 0) {
      setCurrentStep(1);
    } else {
      await validateCurrentPassword(password, username);
    }
  };

  const handleClose = () => {
    setEmail("");
    setPassword("");
    setCurrentStep(0);
    resetErrors();
    close();
  };

  useEffect(() => {
    if (request.message === "The participant was successfully removed") {
      onAfter(participant.participantProfileId);
      handleClose();
    }
    return () => {
      handleClose();
      onAfter(null);
    };
  }, [request]);

  const isDisabledButtonConfirm = () => {
    if (currentStep === 0) {
      return !validateEmail(email);
    }
    return !password;
  };
  const getTitle = () => {
    if (currentStep === 0) {
      return "Update Primary Email";
    }
    if (currentStep === 1) {
      return "Confirm Password";
    }

    return "A verification link has been sent to your email account.";
  };

  const getCancelButtonText = () => {
    if (currentStep === 2) {
      return "Close";
    }
    return "No, I want to keep my current primary email";
  };

  let platforms = "Fruition";

  if (subscriptionStatus?.toLowerCase() === "active") {
    platforms = `${platforms} and Stripe platforms`;
  }

  return (
    <Modal
      onClose={handleClose}
      open={open}
      title={getTitle()}
      confirmButtonText="Yes, update my primary email"
      cancelButtonText={getCancelButtonText()}
      actionConfirmDisabled={isDisabledButtonConfirm()}
      onConfirm={onConfirm}
      hideButtonConfirm={currentStep === 2}
    >
      {currentStep === 0 ? (
        <>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <WarningText>
              You are about to <b>update your primary email</b> from {platforms}{" "}
              and it will no longer be able to access with your old primary
              email. This action cannot be undone, are you sure you want{" "}
              <b>update your primary email</b>?
            </WarningText>
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography
              component="p"
              style={{
                color: "#76838f",
                fontSize: 14,
                lineHeight: 1.36,
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              Please type <b>your new primary email</b> and click on &quot;Yes,
              update my primary email&quot; button.
            </Typography>
          </Grid>
          <Grid
            style={{
              marginBottom: 33,
            }}
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
          >
            <TextField
              fullWidth
              required
              label=""
              name="email"
              id="emailConfirmation"
              inputProps={{
                "data-testid": "email-confirmation",
              }}
              value={email}
              onChange={handleChange}
            />
          </Grid>
        </>
      ) : currentStep === 1 ? (
        <>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <ErrorMessages
              disableScrollToTop
              hideTitle
              errorMessages={errorsForm}
              onClose={resetErrors}
            />
          </Grid>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography
              component="p"
              style={{
                color: "#76838f",
                fontSize: 14,
                lineHeight: 1.36,
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              Please type your <b>current password</b> and click on &quot;Yes,
              update my primary email&quot; button.
            </Typography>
          </Grid>
          <Grid
            style={{
              marginBottom: 33,
            }}
            item
            lg={12}
            md={12}
            sm={12}
            xs={12}
          >
            <TextField
              fullWidth
              required
              label=""
              name="password"
              id="password"
              inputProps={{
                "data-testid": "password",
              }}
              type="password"
              value={password}
              onChange={handleChange}
            />
          </Grid>
        </>
      ) : (
        <>
          <Grid item lg={12} md={12} sm={12} xs={12}>
            <Typography
              component="p"
              style={{
                color: "#76838f",
                fontSize: 14,
                lineHeight: 1.36,
                marginTop: 20,
                marginBottom: 10,
              }}
            >
              Please click on the link that has just been sent to your {` `}
              email account to verify your new primary email.
            </Typography>
          </Grid>
        </>
      )}
    </Modal>
  );
};
